import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ArrowRight, Instagram } from 'lucide-react';

const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/rtsg9i4z_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_26_36%20AM.png';

const HOURS = [
  { day: 'Sunday', time: '10am – 4pm' },
  { day: 'Monday', time: '10am – 10pm' },
  { day: 'Tuesday', time: '10am – 10pm' },
  { day: 'Wednesday', time: '10am – 10pm' },
  { day: 'Thursday', time: '10am – 10pm' },
  { day: 'Friday', time: '10am – 10pm' },
  { day: 'Saturday', time: '10am – 10pm' },
];

const SERVICES = [
  { title: 'Reservations', sub: 'Required' },
  { title: 'Outdoor', sub: 'Seating Available' },
  { title: 'Private', sub: 'Dining Room' },
];

const ContactPage = () => {
  const today = new Date().getDay();

  return (
    <main data-testid="contact-page" className="bg-[#FAF6F0] text-[#1C1917]">
      {/* ============ HERO ============ */}
      <section
        data-testid="contact-hero"
        className="relative min-h-[70vh] md:min-h-[80vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Geet table setting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/70 via-[#1C1917]/55 to-[#FAF6F0]/95" />
        </div>

        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-20 py-32">
          <div className="max-w-3xl text-center mx-auto lg:mx-0 lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A227]" />
              <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm md:text-base tracking-[0.4em] uppercase drop-shadow">
                Visit Us
              </span>
              <span className="h-px w-12 bg-[#C9A227]" />
            </div>

            <h1 className="font-['Marcellus'] text-4xl md:text-6xl lg:text-7xl text-[#FAF6F0] mb-6 leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)]">
              We&apos;ve saved
              <br />
              <span className="italic font-['Cormorant_Garamond'] text-[#FFE7A8]">you a seat.</span>
            </h1>

            <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FAF6F0]/95 max-w-2xl leading-relaxed drop-shadow mx-auto lg:mx-0">
              Pick up the phone, drop by, or follow the lanterns to Brooklyn — we&apos;ll take it
              from there.
            </p>
          </div>
        </div>
      </section>

      {/* ============ AWARD BANNER ============ */}
      <section
        data-testid="contact-award-banner"
        className="relative bg-[#1C1917] text-[#FAF6F0] py-5 md:py-6 overflow-hidden border-y border-[#C9A227]/30"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-5 text-center">
          <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] tracking-[0.4em] uppercase text-xs md:text-sm">
            ✦ Award Winning ✦
          </span>
          <span className="hidden md:block h-5 w-px bg-[#C9A227]/40" />
          <p className="font-['Marcellus'] text-base md:text-xl tracking-wide">
            Voted <span className="text-[#FFE7A8] italic font-['Cormorant_Garamond']">Best Indian Restaurant</span> in South Africa
          </p>
        </div>
      </section>

      {/* ============ CONTACT CARDS + HOURS ============ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#C9A227] opacity-10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#5C4A1F] opacity-10 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Contact cards column */}
            <div className="lg:col-span-7 space-y-6 md:space-y-8">
              {/* Location */}
              <div
                data-testid="contact-location"
                className="bg-[#FAF6F0] border border-[#C9A227]/25 rounded-2xl p-7 md:p-9 hover:border-[#C9A227] hover:shadow-[0_25px_60px_rgba(92,74,31,0.12)] transition-all duration-500"
              >
                <div className="flex items-start gap-5 md:gap-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1C1917] rounded-xl flex items-center justify-center flex-shrink-0 border border-[#C9A227]/40">
                    <MapPin className="text-[#C9A227]" size={26} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-px w-8 bg-[#5C4A1F]" />
                      <span className="font-['Cormorant_Garamond'] italic text-xs tracking-[0.3em] uppercase text-[#5C4A1F]">
                        Location
                      </span>
                    </div>
                    <h3 className="font-['Marcellus'] text-2xl md:text-3xl text-[#1C1917] mb-4 tracking-wide">
                      Brooklyn, Pretoria
                    </h3>
                    <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed mb-5">
                      547 Fehrsen Street<br />
                      Brooklyn Mall, Brooklyn<br />
                      Pretoria 0181 · South Africa
                    </p>
                    <a
                      href="https://maps.app.goo.gl/247H8purqngQ7UWG6"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="get-directions-link"
                      className="group inline-flex items-center gap-2 font-['Cormorant_Garamond'] tracking-[0.2em] uppercase text-sm text-[#5C4A1F] hover:text-[#1C1917] transition-colors"
                    >
                      Get Directions
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div
                data-testid="contact-phone"
                className="bg-[#FAF6F0] border border-[#C9A227]/25 rounded-2xl p-7 md:p-9 hover:border-[#C9A227] hover:shadow-[0_25px_60px_rgba(92,74,31,0.12)] transition-all duration-500"
              >
                <div className="flex items-start gap-5 md:gap-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1C1917] rounded-xl flex items-center justify-center flex-shrink-0 border border-[#C9A227]/40">
                    <Phone className="text-[#C9A227]" size={26} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-px w-8 bg-[#7B2C2C]" />
                      <span className="font-['Cormorant_Garamond'] italic text-xs tracking-[0.3em] uppercase text-[#7B2C2C]">
                        Reservations
                      </span>
                    </div>
                    <h3 className="font-['Marcellus'] text-2xl md:text-3xl text-[#1C1917] mb-4 tracking-wide">
                      Call to book your table
                    </h3>
                    <a
                      href="tel:0124603199"
                      data-testid="phone-link"
                      className="block font-['Marcellus'] text-3xl md:text-4xl text-[#1C1917] hover:text-[#5C4A1F] transition-colors tracking-wider mb-4"
                    >
                      012 460 3199
                    </a>
                    <span className="inline-block border border-[#C9A227]/50 rounded-full px-4 py-1.5 font-['Cormorant_Garamond'] italic text-[#5C4A1F] text-xs tracking-[0.25em] uppercase">
                      Reservations Required
                    </span>
                  </div>
                </div>
              </div>

              {/* Service options */}
              <div
                data-testid="contact-services"
                className="bg-[#1C1917] rounded-2xl p-7 md:p-9 border border-[#C9A227]/30 overflow-hidden relative"
              >
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C9A227] opacity-15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-6 justify-center">
                    <span className="h-px w-10 bg-[#C9A227]" />
                    <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#C9A227]">
                      Service Options
                    </span>
                    <span className="h-px w-10 bg-[#C9A227]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                    {SERVICES.map((item) => (
                      <div
                        key={item.title}
                        className="text-center p-5 rounded-xl border border-[#C9A227]/30 hover:border-[#C9A227] hover:bg-[#C9A227]/5 transition-all duration-300"
                      >
                        <p className="font-['Marcellus'] text-[#C9A227] text-lg md:text-xl tracking-wider mb-1">
                          {item.title}
                        </p>
                        <p className="font-['Cormorant_Garamond'] text-[#FAF6F0]/70 text-sm italic">
                          {item.sub}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Social */}
              <div className="flex items-center justify-center gap-4 text-[#3F3A30]/80">
                <a
                  href="https://www.instagram.com/geet_indianrestaurant/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  aria-label="Geet on Instagram"
                  className="flex items-center gap-2 hover:text-[#5C4A1F] transition-colors font-['Cormorant_Garamond'] italic tracking-wider text-base"
                >
                  <Instagram size={20} />
                  @geet_indianrestaurant
                </a>
              </div>
            </div>

            {/* Hours card */}
            <div className="lg:col-span-5">
              <div
                data-testid="contact-hours"
                className="bg-[#1C1917] rounded-2xl p-7 md:p-9 sticky top-32 border border-[#C9A227]/30 overflow-hidden relative"
              >
                <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#C9A227] opacity-15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 border border-[#C9A227]/50 rounded-xl flex items-center justify-center">
                      <Clock className="text-[#C9A227]" size={24} />
                    </div>
                    <div>
                      <p className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-xs tracking-[0.3em] uppercase">
                        Doors Open
                      </p>
                      <h3 className="font-['Marcellus'] text-2xl text-[#FAF6F0] tracking-wide">
                        Opening Hours
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-0">
                    {HOURS.map((item, index) => {
                      const isToday = index === today;
                      return (
                        <div
                          key={item.day}
                          className={`flex justify-between items-center py-3.5 border-b border-[#C9A227]/15 last:border-0 transition-all ${
                            isToday ? 'bg-[#C9A227]/10 -mx-3 px-3 rounded-lg' : ''
                          }`}
                        >
                          <span
                            className={`font-['Cormorant_Garamond'] text-lg flex items-center gap-3 ${
                              isToday ? 'text-[#C9A227] font-medium' : 'text-[#FAF6F0]'
                            }`}
                          >
                            {item.day}
                            {isToday && (
                              <span className="inline-block border border-[#C9A227] rounded-full px-2.5 py-0.5 text-[10px] tracking-[0.25em] uppercase">
                                Today
                              </span>
                            )}
                          </span>
                          <span
                            className={`font-['Cormorant_Garamond'] text-lg ${
                              isToday ? 'text-[#C9A227]' : 'text-[#FAF6F0]/65'
                            }`}
                          >
                            {item.time}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-center gap-3 mt-8 mb-4">
                    <div className="h-px w-12 bg-[#C9A227]/30" />
                    <span className="text-[#C9A227]">✦</span>
                    <div className="h-px w-12 bg-[#C9A227]/30" />
                  </div>

                  <p className="font-['Cormorant_Garamond'] italic text-base text-[#FAF6F0]/60 text-center">
                    Reservations recommended for the royal experience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MAP ============ */}
      <section
        data-testid="contact-map-section"
        className="relative py-16 md:py-24 bg-[#F3EBDE] overflow-hidden"
      >
        <div className="absolute -top-32 left-1/3 w-[28rem] h-[28rem] rounded-full bg-[#5C4A1F] opacity-10 blur-3xl pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-10 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C9A227]" />
              <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#C9A227]">
                Find Us
              </span>
              <span className="h-px w-10 bg-[#C9A227]" />
            </div>
            <h2 className="font-['Marcellus'] text-3xl md:text-5xl text-[#1C1917] leading-tight">
              The map to <span className="italic font-['Cormorant_Garamond'] text-[#5C4A1F]">our doors.</span>
            </h2>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(92,74,31,0.18)] bg-[#1C1917] p-2 md:p-3">
            <iframe
              data-testid="google-map"
              title="Geet Indian Restaurant Location"
              src="https://maps.google.com/maps?q=-25.7713227,28.2355649&hl=en&z=17&output=embed"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-xl block"
            />
          </div>
        </div>
      </section>

      {/* ============ CTA FOOTER ============ */}
      <section className="relative py-20 md:py-28 bg-[#1C1917] text-[#FAF6F0] overflow-hidden border-t border-[#C9A227]/20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm tracking-[0.35em] uppercase mb-5">
            ✦  Or While You&apos;re Here  ✦
          </p>
          <h2 className="font-['Marcellus'] text-3xl md:text-5xl mb-8 leading-tight">
            Tour the <span className="italic text-[#FFE7A8]">menu &amp; gallery.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/geet/menu"
              data-testid="contact-menu-btn"
              className="group inline-flex items-center gap-3 bg-[#C9A227] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FAF6F0] transition-all duration-300 shadow-lg"
            >
              View the Menu
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/geet/gallery"
              className="inline-flex items-center gap-2 border border-[#C9A227]/60 text-[#C9A227] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg hover:bg-[#C9A227]/10 transition-all duration-300"
            >
              Open the Gallery
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
